import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const ConversionRate = () => {
  // State variables for the conversion rate calculation
  const [conversions, setConversions] = useState("");
  const [totalVisitors, setTotalVisitors] = useState("");
  const [conversionRate, setConversionRate] = useState(0);

  // Event handlers to update state values
  const handleConversionsChange = (e) =>
    setConversions(parseInt(e.target.value) || 0);
  const handleTotalVisitorsChange = (e) =>
    setTotalVisitors(parseInt(e.target.value) || 0);

  // useEffect hook to calculate conversion rate when either conversions or total visitors changes
  useEffect(() => {
    if (conversions && totalVisitors) {
      const rate = (conversions / totalVisitors) * 100;
      setConversionRate(rate);
    } else {
      setConversionRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [conversions, totalVisitors]); // Dependency array: runs when either conversions or total visitors changes

  return (
    <Layout title="Conversion Rate Calculator" path="conversion-rate">
      <section className="section-padding" id="conversion-rate-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Conversion Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Conversion Rate Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Conversions Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="conversions" className="form-label">
                        Number of Conversions
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Conversions:</strong> The total number of successful actions completed by visitors, such as purchases, sign-ups, or downloads."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="conversions"
                        placeholder="Enter number of conversions"
                        value={conversions}
                        onChange={handleConversionsChange}
                      />
                    </div>

                    {/* Total Visitors Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalVisitors" className="form-label">
                        Total Visitors
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Visitors:</strong> The total number of visitors to the website or platform during the specified period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalVisitors"
                        placeholder="Enter total visitors"
                        value={totalVisitors}
                        onChange={handleTotalVisitorsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Conversion Rate:{" "}
                        <span className="fw-bold ms-3">
                          {conversionRate.toFixed(2)}%
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Conversion Rate Calculator
                </h3>

                {/* What is a Conversion Rate Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Conversion Rate Calculator?
                </h4>
                <p>
                  A **Conversion Rate Calculator** is a tool used to determine
                  the percentage of website visitors who complete a desired
                  action (e.g., making a purchase, signing up for a newsletter,
                  etc.). It helps businesses measure the effectiveness of their
                  website and marketing efforts.
                </p>

                {/* Why Use a Conversion Rate Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Conversion Rate Calculator?
                </h4>
                <p>
                  This calculator helps businesses track their marketing
                  performance, identify issues with their website’s conversion
                  process, and set effective goals for increasing conversions
                  and sales.
                </p>

                {/* Benefits of Using a Conversion Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Conversion Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Measures Marketing Effectiveness:</strong>{" "}
                    Understand how well marketing campaigns or website design
                    are converting visitors.
                  </li>
                  <li>
                    <strong>
                      Aids in Identifying Website Performance Issues:
                    </strong>{" "}
                    Spot areas of the site that may need optimization to improve
                    conversion rates.
                  </li>
                  <li>
                    <strong>
                      Helps in Setting and Achieving Marketing Goals:
                    </strong>{" "}
                    Helps businesses set measurable goals for improving
                    conversions and tracking performance over time.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Conversion Rate Calculator?
                </h4>
                <p>
                  The conversion rate calculator is suitable for businesses in:
                </p>
                <ul>
                  <li>
                    <strong>E-commerce:</strong> Helps online stores track how
                    well their site turns visitors into paying customers.
                  </li>
                  <li>
                    <strong>Digital Marketing:</strong> Used by digital
                    marketers to assess the success of campaigns and landing
                    pages.
                  </li>
                  <li>
                    <strong>SaaS Companies:</strong> Ideal for measuring how
                    well a software product converts free trial users to paid
                    subscribers.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is Conversion Rate?</h4>
                <p>
                  Conversion rate is the percentage of visitors to a website who
                  take a desired action. It is calculated by dividing the number
                  of conversions by the total number of visitors, then
                  multiplying the result by 100.
                </p>

                <h4 className="mt-4 mb-3">Why is Conversion Rate Important?</h4>
                <p>
                  Conversion rate is a key metric for understanding the
                  effectiveness of a website or marketing campaign. A higher
                  conversion rate means that more visitors are engaging with the
                  website's content and completing the desired action.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ConversionRate;
